import { AutoComplete, Avatar, Col, Input, InputRef, Row } from 'antd';
import { SearchIcon } from 'assets/Icons/AppointmentIcons';
import Empty from 'components/Empty';
import Text from 'components/Text';
import { get } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import shopSelectors from 'services/shop/selectors';
import { IStaffItemData } from 'services/shop/types/staff';
import styled from 'styled-components';
const ALL_VALUE = 'ALL_VALUE';

type IStaffItemOpt = {
  value: string;
  name: string;
  keywords: string[];
  label: JSX.Element | string;
};

type Props = {
  onPickStaff: (option: IStaffItemData) => void;
  onPickAll: () => void;
  v2?: boolean;
  defaultStaffId?: string;
  showAllOption?: boolean;
  sizeAvatar?: number;
  secondary?: boolean;
  onClearStaff?: () => void;
};
const StaffSearchInput = ({
  onPickStaff,
  onPickAll,
  v2,
  defaultStaffId,
  showAllOption = true,
  sizeAvatar = 28,
  secondary = false,
  onClearStaff,
}: Props) => {
  const inputRef = useRef<InputRef>(null);
  const staffListStore = shopSelectors.data.staffs();

  const initialOptions = useMemo(() => {
    const staffs: IStaffItemOpt[] = [];

    if (showAllOption) {
      staffs.push({
        value: ALL_VALUE,
        name: 'All staffs',
        keywords: [],
        label: (
          <Row align='middle' gutter={[8, 8]}>
            <Col>
              <Avatar>all</Avatar>
            </Col>
            <Col>
              <Text variant='BODY_2'>All staffs</Text>
            </Col>
          </Row>
        ),
      });
    }

    staffListStore.forEach((o) => {
      const staffName = o.nickName ?? '';

      staffs.push({
        value: o.id,
        name: staffName,
        keywords: [staffName?.toLowerCase()],
        label: (
          <Row align='middle' gutter={[8, 8]}>
            <Col>
              <Avatar src={o.urlImage} size={sizeAvatar}>
                {get(o.nickName, [0], '')}
              </Avatar>
            </Col>
            <Col>
              <Text variant='CONTENT_2' style={{ fontFamily: 'Poppins' }}>
                {staffName}
              </Text>
            </Col>
          </Row>
        ),
      });
    });

    return staffs;
  }, [staffListStore, showAllOption]);

  const defaultStaff = useMemo(() => {
    return initialOptions.find((item) => item.value === defaultStaffId);
  }, [initialOptions, defaultStaffId]);

  useEffect(() => {
    if (!defaultStaffId) return;

    const staff = initialOptions.find((item) => item.value === defaultStaffId);

    setSearchText(staff?.name || '');
  }, [defaultStaffId, initialOptions]);

  const [searchText, setSearchText] = useState<string>(defaultStaff?.name || (showAllOption ? 'All staffs' : ''));

  const options = useMemo(() => {
    if (!searchText || (showAllOption && searchText.toLowerCase() === 'all staffs')) return initialOptions;
    return initialOptions.filter((item) =>
      item.value === ALL_VALUE ? true : item.keywords.some((kw) => kw?.includes(searchText?.toLocaleLowerCase())),
    );
  }, [initialOptions, searchText, showAllOption]);

  const handleSwitchCustomer = (data: string) => {
    inputRef?.current?.blur();

    if (data === ALL_VALUE) {
      setSearchText('All staffs');
      onPickAll();
      return;
    }

    const result = staffListStore?.find((item) => item.id === data);
    const staffName = result?.nickName;
    setSearchText(staffName || '');
    if (!result) return;
    onPickStaff(result);
  };

  const handleChange = (value: string) => {
    setSearchText(value);

    if (!value) {
      onClearStaff?.();
    }
  };

  return secondary ? (
    <Container>
      <AutoComplete
        style={{ width: '100%', height: 'unset' }}
        options={options}
        onSelect={handleSwitchCustomer}
        value={searchText}
        onChange={handleChange}
        notFoundContent={<Empty description='Not found' />}
      >
        <InputStyled bordered={false} ref={inputRef} autoFocus placeholder={'Search by Staff’s Name'} />
      </AutoComplete>
      <SearchBox>
        <SearchIcon />
      </SearchBox>
    </Container>
  ) : (
    <AutoComplete
      style={{ width: '100%', height: 'unset' }}
      options={options}
      onSelect={handleSwitchCustomer}
      value={searchText}
      onChange={handleChange}
      notFoundContent={<Empty description='Not found' />}
    >
      <Input
        ref={inputRef}
        autoFocus
        suffix={<SearchIcon fill={v2 ? '#fff' : undefined} />}
        placeholder={v2 ? 'Search by Staff’s Name' : 'Staff name'}
      />
    </AutoComplete>
  );
};

export default StaffSearchInput;

const Container = styled.div`
  display: flex;
  align-self: stretch;
  align-items: center;
  height: 56px;
  gap: 10px;
  flex: 1 0 0;
  border-radius: 100px;
  border: 3px solid #2d6772;
  background: #fff;
`;

const SearchBox = styled.div`
  display: flex;
  width: 56px;
  height: 56px;
  justify-content: center;
  align-items: center;
  aspect-ratio: 1/1;
  border-radius: 0 100px 100px 0;
  background: #2d6772;
`;

const InputStyled = styled(Input)`
  color: #1d2129;
  font-family: Poppins;
  font-size: 18px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
`;
